import { useEffect, useState } from "react";
import { X, PenLine, Signature, CheckCircle2, CircleDashed, ArrowDownToLine, ListChecks, ChevronUp, ChevronDown, Trash2, UserPlus } from "lucide-react";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { dims } from "../../lib/pages";

const pageOf = (f, root, ch) => {
  const pdf = f.closest(".pdf-page");
  if (pdf) return Number(pdf.dataset.page);
  let y = 0;
  for (let el = f; el && el !== root; el = el.offsetParent) y += el.offsetTop;
  const breaks = [...root.querySelectorAll(".page-break")].filter((b) => b.compareDocumentPosition(f) & Node.DOCUMENT_POSITION_FOLLOWING).length;
  return Math.floor(y / ch) + 1 + breaks;
};

export const flashField = (f) => {
  f.scrollIntoView({ block: "center", behavior: "smooth" });
  f.classList.add("sig-flash");
  setTimeout(() => f.classList.remove("sig-flash"), 1600);
};

export const SIGNER_COLORS = ["#0F6CBD", "#7C3AED", "#059669", "#C2410C", "#BE185D", "#0E7490"];

export function applyAssignee(f, signer, idx) {
  if (signer) {
    f.dataset.assigneeId = signer.id;
    f.dataset.assignee = signer.name;
    f.style.setProperty("--sig-color", SIGNER_COLORS[idx % SIGNER_COLORS.length]);
  } else {
    delete f.dataset.assigneeId;
    delete f.dataset.assignee;
    f.style.removeProperty("--sig-color");
  }
  const cap = f.dataset.signed !== "1" && f.querySelector(".sig-caption");
  if (cap) cap.textContent = (f.dataset.kind === "initials" ? "Initials" : "Signature") + (signer ? ` · ${signer.name}` : "");
}

const signerIndex = (f, signers) => {
  const i = signers.findIndex((x) => x.id === f.dataset.assigneeId);
  return i < 0 ? signers.length : i;
};

export const orderedFields = (root, signers) =>
  [...root.querySelectorAll(".sig-field")].map((f, i) => [f, i]).sort((a, b) => signerIndex(a[0], signers) - signerIndex(b[0], signers) || a[1] - b[1]).map(([f]) => f);

const currentSignerIdx = (root, signers) => {
  const open = orderedFields(root, signers).filter((f) => f.dataset.signed !== "1");
  return open.length ? signerIndex(open[0], signers) : signers.length;
};

export function canSign(f, root, s) {
  const signers = s.signers || [];
  if (!signers.length || s.enforceOrder === false || f.dataset.signed === "1" || !f.dataset.assigneeId) return null;
  const cur = currentSignerIdx(root, signers);
  const mine = signerIndex(f, signers);
  return mine > cur && cur < signers.length ? signers[cur].name : null;
}

const Signers = ({ signers, enforce, update, setEnforce }) => {
  const [name, setName] = useState("");
  const add = () => { if (!name.trim()) return; update([...signers, { id: Math.random().toString(36).slice(2, 9), name: name.trim() }]); setName(""); };
  const move = (i, d) => { const a = [...signers]; [a[i], a[i + d]] = [a[i + d], a[i]]; update(a); };
  return (
    <div className="p-4 space-y-2.5 border-b" data-testid="signers-section">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Signing order</span>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer"><Checkbox checked={enforce} onCheckedChange={(v) => setEnforce(!!v)} data-testid="signers-enforce-checkbox" />Enforce order</label>
      </div>
      {signers.map((x, i) => (
        <div key={x.id} className="flex items-center gap-1.5" data-testid={`signer-row-${i}`}>
          <span className="h-6 w-6 shrink-0 rounded-full text-[11px] font-bold text-white flex items-center justify-center" style={{ background: SIGNER_COLORS[i % SIGNER_COLORS.length] }}>{i + 1}</span>
          <Input value={x.name} onChange={(e) => update(signers.map((y) => (y.id === x.id ? { ...y, name: e.target.value } : y)))} className="h-7 text-sm" data-testid={`signer-name-${i}`} />
          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" disabled={!i} onClick={() => move(i, -1)} data-testid={`signer-up-${i}`}><ChevronUp className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" disabled={i === signers.length - 1} onClick={() => move(i, 1)} data-testid={`signer-down-${i}`}><ChevronDown className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-destructive" onClick={() => update(signers.filter((y) => y.id !== x.id))} data-testid={`signer-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      ))}
      <div className="flex gap-1.5">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add signer name" className="h-8 text-sm" data-testid="signer-add-input" />
        <Button size="sm" variant="outline" className="h-8" onClick={add} disabled={!name.trim()} data-testid="signer-add-btn"><UserPlus className="h-3.5 w-3.5 mr-1" />Add</Button>
      </div>
    </div>
  );
};

export const SigningPanel = ({ rootRef, settings, setSettings, onSign, onClose }) => {
  const [, force] = useState(0);
  const [cursor, setCursor] = useState(null);
  useEffect(() => {
    const el = rootRef.current;
    const mo = new MutationObserver(() => force((n) => n + 1));
    mo.observe(el, { subtree: true, childList: true, attributes: true, attributeFilter: ["data-signed", "data-assignee-id"] });
    return () => mo.disconnect();
  }, [rootRef]);

  const root = rootRef.current;
  const signers = settings.signers || [];
  const enforce = settings.enforceOrder !== false;
  const fields = root ? orderedFields(root, signers) : [];
  const ch = dims(settings).ch;
  const done = fields.filter((f) => f.dataset.signed === "1").length;
  const empty = fields.filter((f) => f.dataset.signed !== "1");
  const cur = root ? currentSignerIdx(root, signers) : 0;
  const changed = () => root.dispatchEvent(new Event("input", { bubbles: true }));

  const updateSigners = (list) => {
    root.querySelectorAll(".sig-field").forEach((f) => {
      const i = list.findIndex((x) => x.id === f.dataset.assigneeId);
      applyAssignee(f, list[i], i);
    });
    setSettings({ ...settings, signers: list });
  };
  const assign = (f, id) => { const i = signers.findIndex((x) => x.id === id); applyAssignee(f, signers[i], i); changed(); force((n) => n + 1); };

  const next = () => {
    if (!empty.length) return;
    const at = fields.indexOf(cursor);
    const nxt = fields.find((f, i) => i > at && f.dataset.signed !== "1") || empty[0];
    setCursor(nxt);
    flashField(nxt);
  };

  const groups = [...signers.map((x, i) => ({ key: x.id, title: `${i + 1}. ${x.name}`, color: SIGNER_COLORS[i % SIGNER_COLORS.length], idx: i })), { key: "", title: signers.length ? "Unassigned" : "", idx: signers.length }]
    .map((g) => ({ ...g, items: fields.filter((f) => signerIndex(f, signers) === g.idx) })).filter((g) => g.items.length);

  return (
    <aside className="w-[340px] shrink-0 border-l bg-card flex flex-col" data-testid="signing-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2 font-semibold text-sm"><ListChecks className="h-4 w-4 text-primary" />Signing checklist</div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid="signing-close-btn"><X className="h-4 w-4" /></Button>
      </div>
      <div className="p-4 space-y-3 border-b">
        <div className="flex items-baseline justify-between text-sm">
          <span className="font-medium" data-testid="signing-progress-text">{done} of {fields.length} completed</span>
          <span className="text-xs text-muted-foreground">{empty.length} left</span>
        </div>
        <Progress value={fields.length ? (done / fields.length) * 100 : 0} className="h-1.5" />
        {signers.length > 0 && cur < signers.length && <p className="text-xs" data-testid="signing-current-signer">Now signing: <b style={{ color: SIGNER_COLORS[cur % SIGNER_COLORS.length] }}>{signers[cur].name}</b></p>}
        <Button className="w-full" size="sm" onClick={next} disabled={!empty.length} data-testid="signing-next-btn"><ArrowDownToLine className="h-4 w-4 mr-1.5" />Go to next empty field</Button>
        {fields.length > 0 && !empty.length && <p className="text-xs text-emerald-600 flex items-center gap-1.5" data-testid="signing-complete"><CheckCircle2 className="h-4 w-4" />All fields are signed — ready to export or print.</p>}
      </div>
      <div className="flex-1 overflow-y-auto">
        <Signers signers={signers} enforce={enforce} update={updateSigners} setEnforce={(v) => setSettings({ ...settings, enforceOrder: v })} />
        <div className="p-2" data-testid="signing-list">
          {!fields.length && <p className="text-sm text-muted-foreground p-4 text-center">No signature or initials fields yet. Add them from Insert › Signature / Initials, or click a PDF page.</p>}
          {groups.map((g) => (
            <div key={g.key || "none"} className="mb-2" data-testid={`signing-group-${g.key || "unassigned"}`}>
              {g.title && (
                <div className="flex items-center gap-2 px-3 pt-2 pb-1 text-xs font-semibold" style={{ color: g.color }}>
                  {g.title}<span className="text-muted-foreground font-normal">{g.items.filter((f) => f.dataset.signed === "1").length}/{g.items.length}</span>
                  {g.key && enforce && <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">{g.idx < cur ? "Done" : g.idx === cur ? "Signing now" : "Waiting"}</span>}
                </div>
              )}
              {g.items.map((f) => {
                const i = fields.indexOf(f);
                const signed = f.dataset.signed === "1";
                const initials = f.dataset.kind === "initials";
                const Icon = initials ? Signature : PenLine;
                const waiting = !signed && canSign(f, root, settings);
                return (
                  <div key={i} className={`flex items-center gap-2.5 rounded-lg px-3 py-2 cursor-pointer hover:bg-accent ${cursor === f ? "bg-accent" : ""}`}
                    onClick={() => { setCursor(f); flashField(f); }} data-testid={`signing-item-${i}`}>
                    <Icon className={`h-4 w-4 shrink-0 ${signed ? "text-emerald-600" : "text-primary"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{initials ? "Initials" : "Signature"}{signed && f.dataset.signer ? ` · ${f.dataset.signer}` : ""}</div>
                      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">Page {pageOf(f, root, ch)}
                        {signers.length > 0 && !signed && (
                          <select value={f.dataset.assigneeId || ""} onClick={(e) => e.stopPropagation()} onChange={(e) => assign(f, e.target.value)}
                            className="bg-transparent border rounded px-1 py-0 text-[11px] max-w-[120px]" data-testid={`signing-assign-${i}`}>
                            <option value="">Unassigned</option>
                            {signers.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                          </select>
                        )}
                      </div>
                    </div>
                    {signed
                      ? <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-500/10 rounded px-1.5 py-0.5 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Signed</span>
                      : <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!!waiting} title={waiting ? `Waiting for ${waiting}` : ""}
                          onClick={(e) => { e.stopPropagation(); setCursor(f); flashField(f); onSign(f); }} data-testid={`signing-sign-${i}`}><CircleDashed className="h-3 w-3 mr-1" />{waiting ? "Waiting" : "Sign"}</Button>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
};
